import { useEffect, useState } from 'react';
import { Siren, X, Upload, Loader2, MapPin, PhoneCall, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { emergencySos } from '../lib/api';
import { useAuth } from '../lib/auth';
import ImageUploader from './ImageUploader';

/**
 * Emergency SOS floating action button + dialog.
 * Persistent bottom-right on every page (Needs-First: help must be one tap away).
 */
export default function EmergencySosButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="emergency-sos-fab"
        onClick={() => setOpen(true)}
        className="fixed z-50 bottom-20 right-4 md:bottom-8 md:right-8 inline-flex items-center gap-2 rounded-full bg-[#B71C1C] hover:bg-[#8B0000] text-white px-3 md:px-5 h-11 md:h-14 shadow-xl border-2 border-white ring-4 ring-[#B71C1C]/30 animate-pulse-slow font-black text-sm uppercase tracking-wider"
        title="Emergency: broadcast a medicine SOS to nearby pharmacies"
      >
        <Siren className="w-5 h-5"/>
        SOS
      </button>
      {open && <EmergencySosDialog onClose={() => setOpen(false)}/>}
    </>
  );
}

function EmergencySosDialog({ onClose }) {
  const { user } = useAuth();
  const [medicine, setMedicine] = useState('');
  const [notes, setNotes] = useState('');
  const [rx, setRx] = useState('');
  const [urgency, setUrgency] = useState('high');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [coords, setCoords] = useState(null);

  // Kick off GPS immediately (background)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setCoords({ lat: 28.81815, lng: 79.04198 }), // fallback: Rampur reference
      { timeout: 4000, maximumAge: 60_000 }
    );
  }, []);

  const looksRestricted = /amox|azith|cipro|cef|levof|insul|morph|tram|alpra|diaz|clona|lora|zolp|preg|gaba|warf|clop|pred|steroid|codeine|fenta|opio/i.test(medicine);

  const submit = async () => {
    if (!user) { toast.error('Please sign in to send an emergency SOS'); return; }
    if (medicine.trim().length < 2) { toast.error('Medicine name is required'); return; }
    if (!coords) { toast.error('Getting your location…'); return; }
    if (looksRestricted && !rx) {
      toast.error('This looks like a prescription medicine. Please attach the prescription photo.');
      return;
    }
    setBusy(true);
    try {
      const r = await emergencySos.broadcast({
        medicine_name: medicine.trim(),
        lat: coords.lat, lng: coords.lng,
        radius_km: 5,
        notes: notes.trim() || undefined,
        prescription_url: rx || undefined,
        urgency,
      });
      setResult(r);
      toast.success(`SOS broadcast to ${r.notified} pharmacies within 5 km`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send SOS');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div
        data-testid="emergency-sos-dialog"
        className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-ink/40 hover:text-ink" aria-label="Close">
          <X className="w-5 h-5"/>
        </button>

        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-black text-[#B71C1C]">
          <Siren className="w-4 h-4"/>Emergency medicine SOS
        </div>
        <h3 className="font-heading font-black text-2xl mt-1">Broadcast to nearby pharmacies</h3>
        <p className="text-sm text-ink/70 mt-1">
          Your request goes to every open pharmacy within <b>5 km</b>. Any of them can call you back if they have it.
        </p>

        {result ? (
          <SosSuccessCard result={result} onClose={onClose}/>
        ) : (
          <>
            <div className="mt-4 space-y-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Medicine name *</div>
                <input
                  data-testid="sos-medicine-input"
                  value={medicine}
                  onChange={(e) => setMedicine(e.target.value)}
                  placeholder="e.g. Paracetamol 650 / Insulin / Amoxicillin"
                  className="w-full rounded-lg border-2 border-ink/20 focus:border-ink bg-white px-3 py-2.5 text-base outline-none"
                  autoFocus
                />
                {looksRestricted && (
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-[#B71C1C] font-bold">
                    <ShieldAlert className="w-3 h-3"/>Looks like a prescription-only medicine — please attach a photo below.
                  </div>
                )}
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Urgency</div>
                  <select
                    data-testid="sos-urgency"
                    value={urgency}
                    onChange={(e) => setUrgency(e.target.value)}
                    className="w-full rounded-lg border-2 border-ink/20 focus:border-ink bg-white px-3 py-2.5 text-sm outline-none"
                  >
                    <option value="critical">🆘 Critical (life-threatening)</option>
                    <option value="high">🚨 High (within an hour)</option>
                    <option value="medium">Medium (today)</option>
                    <option value="low">Low (tomorrow ok)</option>
                  </select>
                </label>
                <div className="text-[11px] text-ink/60 flex items-end pb-2">
                  <div className="inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5"/>
                    {coords
                      ? <span>Location ready · 5 km radius</span>
                      : <span className="animate-pulse">Getting location…</span>}
                  </div>
                </div>
              </div>

              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">Notes (optional)</div>
                <textarea
                  data-testid="sos-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. patient 3-year-old, high fever since morning"
                  className="w-full rounded-lg border-2 border-ink/20 focus:border-ink bg-white px-3 py-2 text-sm outline-none"
                />
              </label>

              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink mb-1">
                  Prescription photo {looksRestricted ? '(required)' : '(optional)'}
                </div>
                <ImageUploader
                  testId="sos-prescription-uploader"
                  label="prescription"
                  value={rx}
                  onChange={setRx}
                />
              </div>
            </div>

            <button
              data-testid="sos-submit"
              onClick={submit}
              disabled={busy || medicine.trim().length < 2 || !coords}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#B71C1C] hover:bg-[#8B0000] disabled:bg-ink/25 text-white font-black uppercase tracking-wider text-sm h-12 transition"
            >
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin"/>Broadcasting…</>) : (<><Siren className="w-4 h-4"/>Broadcast SOS</>)}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SosSuccessCard({ result, onClose }) {
  return (
    <div data-testid="sos-success" className="mt-4">
      <div className="rounded-lg bg-[#E8F5E9] border-2 border-[#1B5E20] p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#1B5E20]">
          Broadcast sent ✓
        </div>
        <div className="mt-1 font-heading font-black text-xl text-[#1B5E20]">
          {result.notified} pharmacies alerted
        </div>
        <div className="text-xs text-ink/70 mt-1">
          You&apos;ll get a call back from any pharmacy that has it. Meanwhile, here are the ones nearest to you:
        </div>
      </div>
      <ul className="mt-3 space-y-1.5 max-h-56 overflow-auto">
        {(result.targets || []).map(t => (
          <li key={t.store_id} className="flex items-center gap-2 text-sm rounded-lg border border-ink/10 bg-white p-2.5">
            <MapPin className="w-4 h-4 text-ink/50"/>
            <span className="flex-1 truncate font-bold text-ink">{t.store_name}</span>
            <span className="text-xs font-mono text-[#B71C1C] font-bold">{t.distance_km} km</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex gap-2">
        <a
          href="/emergency/history"
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border-2 border-ink text-ink font-black uppercase text-xs tracking-wider h-11"
        >
          <PhoneCall className="w-4 h-4"/>View responses
        </a>
        <button
          data-testid="sos-close-success"
          onClick={onClose}
          className="flex-1 inline-flex items-center justify-center rounded-lg bg-ink text-white font-black uppercase text-xs tracking-wider h-11"
        >
          Done
        </button>
      </div>
    </div>
  );
}
